// Pure derivations over a company row (the snake_case CSV/API shape).
// Shared by the intel store, segment engine, and segment UI so every surface
// buckets a company the same way.

export const CURRENT_YEAR = 2026 // scrape snapshot year (scrape_date 2026-07-14)


export function keyOf(c) {
  return c.company_id || c.profile_link || c.name
}

export function splitList(value) {
  return String(value || '')
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
}

// The packed `contacts` field: repeated "name=…; role=…; phone=…" groups.
export function contactCount(value) {
  const matches = String(value || '').match(/name=/g)
  return matches ? matches.length : 0
}

// The packed `projects` field: repeated "project_name=…; …" groups.
export function projectMeta(value) {
  const raw = String(value || '')
  if (!raw) return { count: 0, active: 0, lastYear: null }
  const count = (raw.match(/project_name=/g) || []).length
  const active = (raw.match(/project_status=In-Progress/g) || []).length
  let lastYear = null
  for (const m of raw.matchAll(/project_date=[^;]*?((?:19|20)\d{2})/g)) {
    const y = Number(m[1])
    if (y <= CURRENT_YEAR && (!lastYear || y > lastYear)) lastYear = y
  }
  return { count, active, lastYear }
}

export function ageOf(c) {
  const y = Number(c.established)
  if (!y || y < 1800 || y > CURRENT_YEAR) return null
  return CURRENT_YEAR - y
}

export const AGE_BANDS = [
  { value: 'new', label: 'Under 5 yrs' },
  { value: 'growing', label: '5–19 yrs' },
  { value: 'veteran', label: '20+ yrs' },
]

export function ageBandOf(c) {
  const age = ageOf(c)
  if (age == null) return null
  if (age < 5) return 'new'
  if (age < 20) return 'growing'
  return 'veteran'
}

export const SIZE_BANDS = [
  { value: 'micro', label: '1–9 employees' },
  { value: 'small', label: '10–49 employees' },
  { value: 'large', label: '50+ employees' },
]

export function sizeBandOf(c) {
  const m = String(c.company_size || '').match(/^(\d+)/)
  if (!m) return null
  const n = Number(m[1])
  if (n < 10) return 'micro'
  if (n < 50) return 'small'
  return 'large'
}

export const TRADE_BANDS = [
  { value: 'specialist', label: 'Specialist (1–2 trades)' },
  { value: 'multi', label: 'Multi-line (3–7)' },
  { value: 'turnkey', label: 'Turnkey (8+)' },
]

export function tradeBandOf(c) {
  const n = splitList(c.trade).length
  if (!n) return null
  if (n <= 2) return 'specialist'
  if (n <= 7) return 'multi'
  return 'turnkey'
}

export function volumeDisclosed(c) {
  const v = String(c.annual_volume || '').trim()
  return Boolean(v) && !v.startsWith('Undisclosed') && v !== 'Not Provided'
}

export const VOLUME_BANDS = [
  { value: 'sub1m', label: 'Under $1M' },
  { value: 'm1to10', label: '$1M – $10M' },
  { value: 'm10plus', label: '$10M+' },
]

// Disclosed annual_volume comes as buckets — "$1 million - $3 million",
// "< $500,000", "> $50 million" — sometimes with a "DUNS # …" suffix. Band by
// the lower bound of the bucket.
export function volumeBandOf(c) {
  if (!volumeDisclosed(c)) return null
  const m = String(c.annual_volume).match(/\$\s*([\d.,]+)\s*(million)?/i)
  if (!m) return null
  const n = Number(m[1].replace(/,/g, ''))
  if (!Number.isFinite(n) || n <= 0) return null
  const dollars = m[2] ? n * 1e6 : n
  if (dollars < 1e6) return 'sub1m'
  if (dollars < 1e7) return 'm1to10'
  return 'm10plus'
}

// A "proper" website is the company's own domain — not a social profile or a
// shared listing/site-builder placeholder (facebook, yelp, business.site, …).
const NON_COMPANY_HOSTS = [
  'facebook.com', 'instagram.com', 'linkedin.com', 'twitter.com', 'x.com',
  'youtube.com', 'yelp.com', 'google.com', 'business.site', 'godaddy.com',
  'godaddysites.com', 'wixsite.com', 'weebly.com', 'blogspot.com', 'wordpress.com',
  'bbb.org', 'angi.com', 'angieslist.com', 'homeadvisor.com', 'thumbtack.com',
  'nextdoor.com', 'manta.com', 'mapquest.com', 'porch.com', 'houzz.com',
]

export function websiteHost(c) {
  const raw = String(c.website || '').trim()
  if (!raw) return ''
  try {
    return new URL(raw.includes('://') ? raw : `https://${raw}`).hostname.replace(/^www\./, '').toLowerCase()
  } catch {
    return raw.toLowerCase()
  }
}

export const WEBSITE_BANDS = [
  { value: 'proper', label: 'Proper website' },
  { value: 'social', label: 'Social / listing' },
  { value: 'none', label: 'No website' },
]

export function websiteBandOf(c) {
  const host = websiteHost(c)
  if (!host) return 'none'
  return NON_COMPANY_HOSTS.some((h) => host === h || host.endsWith(`.${h}`)) ? 'social' : 'proper'
}

export function certified(c) {
  return splitList(c.certifications).length > 0
}

export const REACH_OPTIONS = [
  { value: 'email', label: 'Has email' },
  { value: 'phone', label: 'Has phone' },
  { value: 'website', label: 'Has website' },
  { value: 'contact', label: 'Named contact' },
]

export function hasReach(c, kind) {
  if (kind === 'email') return Boolean(c.email)
  if (kind === 'phone') return Boolean(c.phone)
  if (kind === 'website') return Boolean(c.website)
  if (kind === 'contact') return contactCount(c.contacts) > 0 || Boolean(c.contact_name)
  return false
}

// Deterministic base intent score, 0–100. Mirrors the spirit of the Data
// section's companySignal but derived from richer buckets so the calibrated
// layer has stable features to shift.
export function baseSignal(c) {
  let s = 20
  if (c.website) s += 12
  if (c.email) s += 16
  if (c.phone) s += 6
  if (volumeDisclosed(c)) s += 12
  const age = ageOf(c)
  if (age != null) s += age >= 20 ? 10 : age >= 5 ? 6 : 3
  const contacts = contactCount(c.contacts)
  if (contacts >= 2) s += 8
  else if (contacts === 1) s += 4
  const proj = projectMeta(c.projects)
  if (proj.active > 0) s += 10
  else if (proj.count > 0) s += 5
  if (sizeBandOf(c)) s += 4
  if (certified(c)) s += 2
  return Math.max(0, Math.min(100, s))
}

// Compact attribute snapshot stored with intel records, so calibration can
// keep working without re-loading the source row.
export function attrsOf(c) {
  return {
    ageBand: ageBandOf(c),
    sizeBand: sizeBandOf(c),
    tradeBand: tradeBandOf(c),
    email: hasReach(c, 'email'),
    website: hasReach(c, 'website'),
    phone: hasReach(c, 'phone'),
    contact: hasReach(c, 'contact'),
    volume: volumeDisclosed(c),
    projects: projectMeta(c.projects).count,
    base: baseSignal(c),
  }
}

// Directory websites are stored bare ("acmeanalytics.com") — as an href that's a
// RELATIVE url, so "Visit website" opened this app in the new tab instead of
// the company's site. Always hand anchors an absolute URL.
export function siteUrl(raw) {
  const s = String(raw || '').trim()
  if (!s) return ''
  return /^https?:\/\//i.test(s) ? s : `https://${s.replace(/^\/+/, '')}`
}

// ── outreach state, from the HubSpot mirror's per-company rollup ────────────
//
// The two questions the leads page asks of a row — have we reached out, and did
// they write back — answered from the mirror's own counts (Rollup.email_out /
// email_in, computed by hubspot-sync#_company_rollup). Never from a local
// guess: this app has never sent an email, so HubSpot is the only witness.
//
// FOUR STATES, and the difference between the last two is the one that matters:
//   'replied'  an inbound email is on the record
//   'sent'     outbound only (or emails whose direction the mirror never
//              recorded — they prove contact happened, not who started it)
//   'none'     the company IS matched in HubSpot and carries no email at all
//   'unknown'  no matched CRM record, so nothing can be claimed either way —
//              emphatically NOT the same as "never contacted"
export function outreachOf(company, sent = null) {
  // TWO SOURCES, AND THE ANSWER IS THE UNION. HubSpot knows the emails somebody
  // logged to it; the rep's own sent folder knows the rest. Judging on HubSpot
  // alone reported companies he had personally emailed as never contacted —
  // and the cadence then offered them a cold intro. `sent` is the sent-folder
  // index (lib/sentMail.js); absent, this behaves exactly as it used to.
  const hs = company?._hubspot
  const gmail = sent ? sent.matchRow(company) : null
  const rollup = (hs && hs.matched && hs.rollup) || {}
  const out = Number(rollup.email_out) || 0
  const inbound = Number(rollup.email_in) || 0
  const total = Number(rollup.email_count) || 0
  const lastInboundAt = String(rollup.last_inbound_at || '')
  if (inbound > 0) {
    return { state: 'replied', out: out || gmail?.n || 0, in: inbound, lastInboundAt, via: 'hubspot' }
  }
  if (out > 0 || total > 0) {
    return { state: 'sent', out: out || total, in: 0, lastInboundAt: '', via: 'hubspot' }
  }
  // Sent from the mailbox and never logged to the CRM — a real touch that
  // HubSpot simply never heard about.
  if (gmail && gmail.n > 0) {
    return { state: 'sent', out: gmail.n, in: 0, lastInboundAt: '', via: 'gmail', lastSentAt: gmail.last }
  }
  if (!hs || !hs.matched) return { state: 'unknown', out: 0, in: 0, lastInboundAt: '' }
  return { state: 'none', out: 0, in: 0, lastInboundAt: '' }
}

export const OUTREACH_LABEL = {
  replied: 'Replied',
  sent: 'Reached out',
  none: 'Not contacted',
  unknown: 'No CRM record',
}
